import { Reveal, MaskLines } from "./Reveal";
import { CtaButton } from "./CtaButton";
import {
  LineChart,
  CandlestickChart,
  ShieldCheck,
  Brain,
  Briefcase,
  Gem,
  Check,
} from "lucide-react";

const MODULES = [
  {
    icon: LineChart,
    title: "Market Foundations",
    items: [
      "How markets really move: liquidity, order flow and supply & demand",
      "Reading price action and market structure",
      "Trends, ranges, breakouts and key levels",
      "Multi-timeframe analysis and bias",
      "Sectors, catalysts and how news moves price",
    ],
  },
  {
    icon: CandlestickChart,
    title: "Technical Analysis",
    items: [
      "Candlestick patterns and confirmation",
      "Support, resistance and supply/demand zones",
      "Moving averages, VWAP and momentum",
      "Volume analysis and tape reading",
      "High-probability chart patterns and entries",
    ],
  },
  {
    icon: Gem,
    title: "Strategy By Market",
    items: [
      "Day trading equities and momentum",
      "Swing trading multi-day setups",
      "Options: calls, puts, spreads and premium",
      "Futures: index and commodity contracts",
      "Bitcoin and crypto trading through the cycles",
      "Building and back-testing a rules-based system",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Risk & Money Management",
    items: [
      "Position sizing and the 1% rule",
      "Stop placement and R-multiple thinking",
      "Managing drawdowns without blowing up",
      "Portfolio heat, correlation and exposure",
      "Compounding and safely scaling account size",
    ],
  },
  {
    icon: Brain,
    title: "Trading Psychology",
    items: [
      "Discipline, patience and emotional control",
      "Beating fear, greed, revenge trading and FOMO",
      "Journaling, review and pattern-spotting",
      "Building unshakeable daily routines",
      "Identity work: becoming the trader you want to be",
    ],
  },
  {
    icon: Briefcase,
    title: "The Business Of Trading",
    items: [
      "Pre-market prep, watchlists and game plans",
      "Execution, platforms and broker workflow",
      "Record keeping, taxes and running trading like a business",
      "Turning consistency into reliable monthly income",
      "Converting profits into real assets and freedom",
    ],
  },
];

export const Curriculum = () => (
  <section id="curriculum" className="relative border-t border-white/10 py-20 sm:py-24">
    <div
      className="pointer-events-none absolute left-1/2 top-10 h-[45vh] w-[45vh] -translate-x-1/2 rounded-full opacity-[0.1] blur-3xl"
      style={{ background: "radial-gradient(circle, #d4af37, transparent 65%)" }}
    />
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">
            Inside The School &mdash; What&rsquo;s Taught
          </span>
        </Reveal>
        <h2 className="mt-8 font-['Playfair_Display'] text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
          <MaskLines whileInView lineClass="gold-metallic-text" lines={["Everything a serious", "trader needs to master."]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-7 text-base leading-relaxed text-[#a1a1aa] sm:text-lg">
            This is the full curriculum you&rsquo;ll be coached through. As a hands-on{" "}
            <strong className="text-[#f5f5f0]">6 figure stock trading coach</strong>, Shabz doesn&rsquo;t
            hand you a video library and disappear &mdash; every module below is taught, applied and
            pressure-tested against your real trading, in your real market conditions.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
        {MODULES.map((m, i) => (
          <Reveal key={m.title} delay={(i % 3) * 0.08}>
            <div
              data-testid={`module-${i}`}
              className="gold-toptrim flex h-full flex-col gap-5 bg-[#0e0e10] p-8 transition-colors duration-300 hover:bg-[#141416]"
            >
              <div className="flex items-center gap-4">
                <m.icon className="h-6 w-6 shrink-0 text-[#d4af37]" strokeWidth={1.3} />
                <h3 className="font-['Playfair_Display'] text-xl text-[#f5f5f0]">{m.title}</h3>
              </div>
              <ul className="space-y-3">
                {m.items.map((it, j) => (
                  <li key={j} data-testid={`module-${i}-item-${j}`} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#d4af37]" strokeWidth={2.2} />
                    <span className="text-sm leading-relaxed text-[#a1a1aa]">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-14 flex justify-center">
        <CtaButton testid="curriculum-cta" />
      </Reveal>
    </div>
  </section>
);
